
import React, { useState, useMemo, useEffect } from 'react';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import { Language } from '../../types';
import { t } from '../../i18n';

interface ReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    isLoading?: boolean;
    lang: Language;
    title?: string;
}

export const ReportModal: React.FC<ReportModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
    lang,
    title
}) => {
    const [selectedReason, setSelectedReason] = useState("");
    const [customReason, setCustomReason] = useState("");
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setIsAnimating(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsAnimating(false);
            setSelectedReason("");
            setCustomReason("");
        }
    }, [isOpen]);

    const REASONS = useMemo(() => [
        { id: "Spam or misleading", label: t(lang, 'reportReasonSpam') },
        { id: "Harassment or hate speech", label: t(lang, 'reportReasonHarassment') },
        { id: "Inappropriate content", label: t(lang, 'reportReasonInappropriate') },
        { id: "Violence or harmful behavior", label: t(lang, 'reportReasonViolence') },
        { id: "Other", label: t(lang, 'reportReasonOther') }
    ], [lang]);

    if (!isOpen) return null;

    const handleClose = () => {
        if (isLoading) return;
        setIsAnimating(false);
        setTimeout(onClose, 500);
    };

    const handleSubmit = () => {
        const finalReason = selectedReason === "Other" ? customReason : selectedReason;
        if (!finalReason) return;
        onConfirm(finalReason);
    };

    return (
        <div className={`fixed inset-0 z-[500] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose}></div>
            
            {/* Modal Container - 바텀 시트 스타일 애니메이션 적용 */}
            <div className={`relative bg-white w-full md:max-w-[440px] rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0' : 'translate-y-full md:scale-95'}`}>
                
                {/* Mobile Handle Bar */}
                <div className="w-12 h-1.5 bg-slate-100 rounded-full mx-auto my-4 md:hidden"></div>

                <div className="p-8 pt-4 md:pt-8 flex flex-col">
                    <button 
                        onClick={handleClose}
                        className="absolute top-6 right-6 text-slate-400 md:hover:text-slate-900 transition-colors p-2"
                    >
                        <X size={24} strokeWidth={2.5} />
                    </button>

                    <div className="flex flex-col items-center text-center mb-8">
                        <div className="w-16 h-16 bg-rose-50 rounded-[24px] flex items-center justify-center text-rose-500 mb-4 shadow-sm">
                            <AlertTriangle size={32} strokeWidth={2.5} />
                        </div>
                        <h3 className="text-2xl font-black text-slate-900 leading-tight uppercase tracking-tight">
                            {title || t(lang, 'reportTitle')}
                        </h3>
                        <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mt-1.5">
                            {t(lang, 'reportDesc')}
                        </p>
                    </div>

                    <div className="space-y-2 mb-10 overflow-y-auto max-h-[50vh] px-1 custom-scrollbar">
                        {REASONS.map((reason) => (
                            <button
                                key={reason.id}
                                onClick={() => setSelectedReason(reason.id)}
                                className={`w-full text-left px-6 py-4 rounded-[22px] text-sm font-black transition-all border-2 uppercase tracking-tight ${
                                    selectedReason === reason.id 
                                    ? 'bg-slate-900 text-white border-slate-900 shadow-xl' 
                                    : 'bg-slate-50 text-slate-500 border-transparent md:hover:bg-slate-100'
                                }`}
                            >
                                {reason.label}
                            </button>
                        ))}

                        {selectedReason === "Other" && (
                            <textarea
                                className="w-full mt-2 bg-slate-50 border-2 border-slate-100 rounded-3xl p-5 text-sm font-bold text-slate-900 focus:outline-none focus:border-rose-200 transition-all placeholder:text-slate-300 resize-none animate-in slide-in-from-top-2"
                                placeholder={t(lang, 'reportOtherPlaceholder')}
                                rows={3}
                                value={customReason}
                                onChange={(e) => setCustomReason(e.target.value)}
                            />
                        )}
                    </div>

                    <div className="flex gap-4">
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className="flex-1 py-4 text-xs font-black uppercase tracking-[0.2em] text-slate-400 md:hover:text-slate-600 transition-all"
                        >
                            {t(lang, 'cancel')}
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isLoading || !selectedReason || (selectedReason === "Other" && !customReason.trim())}
                            className="flex-[2] bg-rose-500 text-white py-5 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] md:hover:bg-rose-600 active:scale-95 transition-all shadow-xl shadow-rose-100 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {isLoading ? <Loader2 size={18} className="animate-spin" /> : t(lang, 'submitReport')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
